import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { axiosDelete, axiosGet, axiosPost, axiosPut } from 'Redux/axiosHelper'
import {
  LeagueGamePayloadType,
  LeagueGameRespType,
  LeagueGamesByRoundDate,
} from 'types/league/leagueGame'

//common component for updating cache
const updateLeagueGamesCache = (
  queryClient: any,
  newGame: LeagueGameRespType,
) => {
  const getWeekday = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long' })

  // Use UTC date to match backend grouping (backend uses toISOString().split('T')[0])
  const gameUtcDate = moment.utc(newGame.date).format('YYYY-MM-DD')

  queryClient.setQueryData(
    ['league-games-listing'],
    (oldData: LeagueGamesByRoundDate[] | undefined) => {
      if (!oldData || oldData.length === 0) {
        return [
          {
            round: newGame.round,
            dates: [
              {
                date: gameUtcDate,
                day: getWeekday(newGame.date),
                games: [newGame],
              },
            ],
          },
        ]
      }

      // Step 1: Remove the game if it exists anywhere (important for update)
      let workingData = [...oldData]
      for (let r = 0; r < workingData.length; r++) {
        const roundGroup = workingData[r]
        for (let d = 0; d < roundGroup.dates.length; d++) {
          const dateGroup = roundGroup.dates[d]
          const gIndex = dateGroup.games.findIndex(g => g._id === newGame._id)
          if (gIndex !== -1) {
            // Remove game
            const newGames = [
              ...dateGroup.games.slice(0, gIndex),
              ...dateGroup.games.slice(gIndex + 1),
            ]
            let newDates =
              newGames.length > 0
                ? [
                    ...roundGroup.dates.slice(0, d),
                    { ...dateGroup, games: newGames },
                    ...roundGroup.dates.slice(d + 1),
                  ]
                : [
                    ...roundGroup.dates.slice(0, d),
                    ...roundGroup.dates.slice(d + 1),
                  ]

            const newRound: LeagueGamesByRoundDate = {
              ...roundGroup,
              dates: newDates,
            }

            workingData = [
              ...workingData.slice(0, r),
              newRound,
              ...workingData.slice(r + 1),
            ]

            // Remove empty round if needed
            if (newRound.dates.length === 0) {
              workingData = [
                ...workingData.slice(0, r),
                ...workingData.slice(r + 1),
              ]
            }

            break
          }
        }
      }

      // Step 2: Insert into correct round/date
      const roundIndex = workingData.findIndex(
        r => Number(r.round) === Number(newGame.round),
      )

      if (roundIndex === -1) {
        return [
          ...workingData,
          {
            round: newGame.round,
            dates: [
              {
                date: gameUtcDate,
                day: getWeekday(newGame.date),
                games: [newGame],
              },
            ],
          },
        ]
      }

      const roundGroup = workingData[roundIndex]
      const dateIndex = roundGroup.dates.findIndex(
        d => d.date === gameUtcDate,
      )

      if (dateIndex === -1) {
        const updatedRound: LeagueGamesByRoundDate = {
          ...roundGroup,
          dates: [
            ...roundGroup.dates,
            {
              date: gameUtcDate,
              day: getWeekday(newGame.date),
              games: [newGame],
            },
          ],
        }
        return [
          ...workingData.slice(0, roundIndex),
          updatedRound,
          ...workingData.slice(roundIndex + 1),
        ]
      }

      const dateGroup = roundGroup.dates[dateIndex]
      const existingIndex = dateGroup.games.findIndex(
        g => g._id === newGame._id,
      )
      const mergedGames =
        existingIndex === -1
          ? [...dateGroup.games, newGame]
          : [
              ...dateGroup.games.slice(0, existingIndex),
              newGame,
              ...dateGroup.games.slice(existingIndex + 1),
            ]

      const finalRound: LeagueGamesByRoundDate = {
        ...roundGroup,
        dates: [
          ...roundGroup.dates.slice(0, dateIndex),
          { ...dateGroup, games: mergedGames },
          ...roundGroup.dates.slice(dateIndex + 1),
        ],
      }

      return [
        ...workingData.slice(0, roundIndex),
        finalRound,
        ...workingData.slice(roundIndex + 1),
      ]
    },
  )
}

// get legaue games
const getLeagueGames = async (params: {
  leagueId: string
  organizationId?: string
}) => {
  const response = await axiosGet(`v1/league-games-schedule`, params)
  return response.data
}
export const useGetLeagueGames = (params: {
  leagueId: string
  organizationId?: string
}) => {
  const gamesList = useQuery<LeagueGamesByRoundDate[]>(
    ['league-games-listing'],
    () => getLeagueGames(params),
    { enabled: false },
  )
  return gamesList
}

//get league game by id
const getLeagueGameById = async (gameId: string) => {
  const response = await axiosGet(`v1/league-games-schedule/${gameId}`)
  return response.data
}
export const useGetGameById = (gameId: string) => {
  const gameById = useQuery<LeagueGamesByRoundDate[]>(
    ['league-game-by-id'],
    () => getLeagueGameById(gameId),
    { enabled: false },
  )
  return gameById
}

//create league game
const createLeagueGame = async (payload: LeagueGamePayloadType) => {
  const response = await axiosPost(payload, `v1/league-games-schedule`)
  return response.data
}

export const useCreateLeagueGame = () => {
  const queryClient = useQueryClient()
  return useMutation(createLeagueGame, {
    onSuccess: (newGame: LeagueGameRespType) => {
      updateLeagueGamesCache(queryClient, newGame)
      queryClient.invalidateQueries(['league-standings-by-id'])
    },
  })
}

//update league game
const updateLeagueGame = async ({
  payload,
  gameId,
}: {
  payload: LeagueGamePayloadType
  gameId: string
}) => {
  const response = await axiosPut(payload, `v1/league-games-schedule/${gameId}`)
  return response.data
}

export const useUpdateLeagueGame = () => {
  const queryClient = useQueryClient()
  return useMutation(updateLeagueGame, {
    onSuccess: (updatedGame: LeagueGameRespType) => {
      updateLeagueGamesCache(queryClient, updatedGame)
      queryClient.invalidateQueries(['league-standings-by-id'])
    },
  })
}

// delete game by id
const deleteGameById = async (gameId: string) => {
  const response = await axiosDelete(`v1/league-games-schedule/${gameId}`)
  return response.data
}
export const useDeleteGameById = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteGameById, {
    onSuccess: (_data: LeagueGameRespType, deletedGameId: string) => {
      queryClient.setQueryData(
        ['league-games-listing'],
        (oldData: LeagueGamesByRoundDate[] | undefined) => {
          if (!oldData || oldData.length === 0) return oldData
          const updatedRounds = oldData
            .map(roundGroup => {
              const updatedDates = roundGroup.dates
                .map(dateGroup => ({
                  ...dateGroup,
                  games: dateGroup.games.filter(g => g._id !== deletedGameId),
                }))
                .filter(dateGroup => dateGroup.games.length > 0)

              return { ...roundGroup, dates: updatedDates }
            })
            .filter(roundGroup => roundGroup.dates.length > 0)

          return updatedRounds
        },
      )
      queryClient.invalidateQueries(['league-standings-by-id'])
    },
  })
}
